import React from "react";
import { newId } from "./lib/ids";
import { EmptyState, Icon, Portal, quotePayments, HubLinkButton, HubRegenButton, genToken, notify, confirmDialog, FollowUp, clickable, useNarrowScreen, downloadExport } from "./registry";
import { VerticalChip } from "./registry";   // "Concrete" on a job from the other service; nothing on a fence job
import { fmtMeasure, verticalOf } from "./lib/verticals";
import { buildConcreteQuote } from "./lib/engine.concrete";   // a care plan books as a fresh estimate off today's rate cards
import { rebookBlocks, planLabel, planDue, addMonths, sinceLabel, CARE_TYPES, CARE_LABEL, CARE_VERB, CARE_EVERY, everyLabel } from "./lib/careplans";   // recurring care plans (§13 item 15)
import { downscaleToDataUrl } from "./lib/img";
import { localYmd } from "./lib/dates";

/* the header name field edits a local draft and commits on blur / Enter — a
   keystroke-per-store-write here re-rendered the whole CRM (and a cloud save)
   on every letter, and could drop characters mid-word on a slow sync. */
function NameInput({ value, onCommit }: any) {
  const [draft, setDraft] = React.useState<any>(value || "");
  React.useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = () => onCommit((draft || "").trim() || value || "");
  return (
    <input className="cust-name-input" value={draft} placeholder="Customer name"
      onChange={e => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { setDraft(value || ""); (e.target as HTMLInputElement).blur(); } }} />
  );
}
/* ============================================================
   Fence Works — Customers (CRM, editable)
   Every quote / project rolls up to a customer record. Edit
   contact info, add notes, see all their jobs & payments.
   ============================================================ */
const CU_money0 = window.VFP.money0;

/* ---- customer tags ----
   Freeform labels on a customer (VIP / Repeat / Commercial / Warranty …),
   stored as `customer.tags: string[]` and edited through updateCustomer.
   A small suggested set seeds the picker; the operator can type anything. */
const SUGGESTED_TAGS = ["VIP", "Repeat", "Commercial", "Residential", "Warranty", "Referral", "Net 30"];
const tagsOf = (c: any): string[] => Array.isArray(c && c.tags) ? c.tags.filter(Boolean) : [];
// add a tag (trimmed, deduped case-insensitively); returns the next array
function addTag(list: string[], raw: string): string[] {
  const t = (raw || "").trim();
  if (!t) return list;
  if (list.some(x => x.toLowerCase() === t.toLowerCase())) return list;
  return [...list, t];
}
const removeTag = (list: string[], t: string): string[] => list.filter(x => x !== t);

function jobsForCustomer(store, c) {
  const byId = (arr) => (arr || []).filter(x => x.customerId === c.id);
  const byName = (arr) => (arr || []).filter(x => !x.customerId && (x.customer || "").trim().toLowerCase() === (c.name || "").trim().toLowerCase());
  const quotes = [...byId(store.quotes), ...byName(store.quotes)];
  const projects = [...byId(store.projects), ...byName(store.projects)];
  const workOrders = (store.workOrders || []).filter(w => w.customerId === c.id || quotes.some(q => q.id === w.quoteId));
  // jobs with no quote or project behind them (imported history, jobs added by
  // hand) are won work on their own — counted once, never alongside a project
  const looseJobs = workOrders.filter(w => !w.quoteId && !w.projectId);
  return { quotes, projects, workOrders, looseJobs };
}

/* When the customer really started: the "customer since" an import brought
   (YYYY-MM-DD) when it's earlier than the record's own createdAt. */
function customerSinceMs(c: any): number {
  const created = Number(c.createdAt) || Date.now();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(c.customerSince || ""));
  if (!m) return created;
  const t = new Date(+m[1], +m[2] - 1, +m[3], 12).getTime();
  return Number.isFinite(t) && t < created ? t : created;
}

// compose the single-line `address` from the structured parts — everything that
// prints a customer address reads `address`, so keep it the source of truth.
function composeAddress(p: any) {
  return [p.street, [p.city, p.state].filter(Boolean).join(", "), p.zip].filter(Boolean).join(" ").replace(/\s+,/g, ",").trim();
}

/* ---- duplicate detection ----
   Two records are likely the same person when they share a normalized name,
   email, or phone. We group transitively (A~B, B~C ⇒ {A,B,C}) with union-find
   so a chain of near-matches collapses into one cluster the operator can merge. */
const normName = (s: any) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const normEmail = (s: any) => (s || "").trim().toLowerCase();
const normPhone = (s: any) => (s || "").replace(/\D/g, "");   // digits only
function findDuplicateGroups(customers: any[]) {
  const parent: Record<string, string> = {};
  const find = (x: string): string => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a: string, b: string) => { parent[find(a)] = find(b); };
  customers.forEach(c => { parent[c.id] = c.id; });
  // bucket by each normalized key; any two sharing a non-empty key are linked
  const buckets: Record<string, string[]> = {};
  for (const c of customers) {
    const keys = [normName(c.name) && "n:" + normName(c.name), normEmail(c.email) && "e:" + normEmail(c.email), normPhone(c.phone) && "p:" + normPhone(c.phone)].filter(Boolean) as string[];
    for (const k of keys) { (buckets[k] = buckets[k] || []).push(c.id); }
  }
  for (const ids of Object.values(buckets)) for (let i = 1; i < ids.length; i++) union(ids[0], ids[i]);
  // collect clusters with more than one member
  const groups: Record<string, any[]> = {};
  for (const c of customers) { const r = find(c.id); (groups[r] = groups[r] || []).push(c); }
  return Object.values(groups).filter(g => g.length > 1);
}

/* ---- job-history timeline ----
   Merge everything that has already happened on a customer into ONE
   reverse-chronological feed. Nothing new is persisted — every event is
   *derived* from records that already exist: the quotes / projects / work
   orders rolled up in `c._jobs`, plus the customer's noteLog, tasks, and the
   payment data carried on each quote. Each event is { ts, icon, label, sub?,
   go? } where `ts` is a millisecond timestamp used only for sorting and the
   day label, and `go` (when present) deep-links to the underlying record.

   Several records don't persist an exact timestamp for a state change (a quote
   has no "signedAt", a work order no "completedAt"), so we fall back to the
   best date that *is* on the record — a payment date, scheduledEnd/Start, or
   createdAt — and degrade gracefully rather than invent history. */
function tsOfDateStr(s: any): number {     // "2026-06-14" → ms (noon, local) or 0
  if (!s) return 0;
  const t = Date.parse(typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + "T12:00:00" : s);
  return isFinite(t) ? t : 0;
}
function buildTimeline(c: any, jobs: any, go: any, openTab: (id: string) => void) {
  const { quotes, projects, workOrders } = jobs;
  const ev: any[] = [];
  const push = (ts: number, icon: string, label: string, extra?: any) => { if (ts) ev.push({ ts, icon, label, ...extra }); };

  (workOrders || []).forEach((w: any) => {
    if (w.reviewRequestedAt) push(w.reviewRequestedAt, "send", "Review request sent", { sub: "Google review · automatic email", go: () => go("workorders", { openWorkOrder: w.id }) });
    if (w.reviewReceived) push(w.reviewReceivedAt || w.reviewRequestedAt, "check", "Review received ★", { go: () => go("workorders", { openWorkOrder: w.id }) });
    if (w.referralRequestedAt) push(w.referralRequestedAt, "user", "Referral ask sent", { sub: "$100 bonus · 4-email sequence", go: () => go("workorders", { openWorkOrder: w.id }) });
    if (w.warrantyRegisteredAt) push(w.warrantyRegisteredAt, "check", "Warranty registered", { sub: (w.warrantyYears || 1) + "-yr · checkup task scheduled" });
  });
  quotes.forEach((q: any) => {
    const open = () => go("quote", { quoteId: q.id });
    push(q.createdAt, "send", "Quote created — " + (q.productName || "estimate"),
      { sub: (q.lf ? Math.round(q.lf) + " ft · " : "") + CU_money0(q.total || 0), val: q.total, go: open });
    // every send is its own event (a quote can be re-sent); the first send often
    // shares createdAt, so skip a send that lands on the create moment.
    const sends = Array.isArray(q.sends) ? q.sends : [];
    sends.forEach((s: any) => { if (s && s.at && s.at !== q.createdAt) push(s.at, "mail", "Quote sent to customer", { go: open }); });
    // signed/paid: no signedAt is stored → date it from the latest payment, else lastSentAt
    if (q.status === "signed" || q.status === "paid") {
      const lastPayAt = Math.max(0, ...(Array.isArray(q.payments) ? q.payments.map((p: any) => p.at || 0) : []));
      const when = lastPayAt || q.lastSentAt || q.createdAt;
      push(when, "check", "Quote " + q.status, { sub: CU_money0(q.total || 0), go: open });
    }
    // manual payments recorded on the quote (cash / check / outside card)
    (Array.isArray(q.payments) ? q.payments : []).forEach((p: any) => {
      push(p.at || 0, "dollar", "Payment received" + (p.method ? " · " + p.method : ""),
        { sub: CU_money0(p.amount || 0), val: p.amount, go: open });
    });
    // online (Stripe) payments that carry their own date
    (Array.isArray(q.stripePayments) ? q.stripePayments : []).forEach((p: any) => {
      push(p.at || 0, "dollar", "Online payment", { sub: CU_money0(p.amount || 0), val: p.amount, go: open });
    });
    // change orders adjust the signed total mid-job
    (Array.isArray(q.changeOrders) ? q.changeOrders : []).forEach((co: any) => {
      push(co.at || 0, "edit", "Change order" + (co.label ? " — " + co.label : ""), { sub: CU_money0(co.amount || 0), go: open });
    });
  });

  // projects (won jobs) — quote-derived projects share the quote timestamp, so
  // only surface ones that don't already trace back to a listed quote.
  const quoteIds = new Set(quotes.map((q: any) => q.id));
  projects.forEach((p: any) => {
    if (p.fromQuote && quoteIds.has(p.fromQuote)) return;
    push(p.createdAt, "check", "Job won — " + (p.productName || p.job || "project"), { sub: CU_money0(p.total || 0), val: p.total });
  });

  workOrders.forEach((w: any) => {
    const open = () => go("workorders");
    if (!w.imported) push(w.createdAt, "clipboard", "Work order created — " + (w.title || "job"), { go: open });
    if (w.scheduledStart) push(tsOfDateStr(w.scheduledStart), "calendar", "Job scheduled", { sub: w.title || "", go: open });
    // completed has no timestamp → best-effort date = end/start of the scheduled window
    if (w.status === "completed") push(tsOfDateStr(w.scheduledEnd || w.scheduledStart) || w.createdAt, "check", "Job completed — " + (w.title || "job"), { sub: CU_money0(w.total || 0), val: w.total, go: open });
  });

  // CRM notes & follow-ups live on the customer record
  (Array.isArray(c.noteLog) ? c.noteLog : []).forEach((n: any) => {
    push(n.createdAt, "edit", "Note: " + (n.text || "").slice(0, 80), { go: () => openTab("notes") });
  });
  (Array.isArray(c.tasks) ? c.tasks : []).forEach((t: any) => {
    push(t.createdAt, "clipboard", "Follow-up added: " + (t.text || "").slice(0, 70), { go: () => openTab("notes") });
    if (t.done && t.doneAt) push(t.doneAt, "check", "Follow-up done: " + (t.text || "").slice(0, 70), { go: () => openTab("notes") });
  });

  // customer record itself anchors the bottom of the feed
  push(customerSinceMs(c), "user", "Customer added");

  ev.sort((a, b) => b.ts - a.ts);
  return ev;
}

function CustomerForm({ initial, onSave, onClose }: any) {
  const [f, setF] = React.useState<any>(() => {
    const v: any = { name: (initial && initial.name) || "", phone: (initial && initial.phone) || "", email: (initial && initial.email) || "",
      address: (initial && initial.address) || "", street: (initial && initial.street) || "", city: (initial && initial.city) || "", state: (initial && initial.state) || "", zip: (initial && initial.zip) || "" };
    // legacy customers carry only a single-line `address` → seed the street field from it
    if (!v.street && !v.city && !v.state && !v.zip && v.address) v.street = v.address;
    return v;
  });
  const up = (k: string) => (e: any) => setF((s: any) => ({ ...s, [k]: e.target.value }));
  const canSave = !!((f.name || "").trim() || (f.email || "").trim() || (f.phone || "").trim());
  function emit() {
    const composed = composeAddress(f);
    onSave({ ...f, address: composed || f.address || "" });
  }
  return (
    <Portal>
    <div data-dialog onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.55)", display: "grid", placeItems: "center", padding: 16 }}>
      <div className="card pad" onClick={(e: any) => e.stopPropagation()} style={{ maxWidth: 440, width: "100%" }}>
        <div className="kicker kicker-teal" style={{ marginBottom: 12 }}><Icon name="user" size={14} />{initial ? "Edit customer" : "New customer"}</div>
        <div style={{ display: "grid", gap: 10 }}>
          <div><label className="field-label">Name</label><input className="input" autoFocus value={f.name} onChange={up("name")} placeholder="Customer name" /></div>
          <div className="row gap-8">
            <div style={{ flex: 1 }}><label className="field-label"><Icon name="phone" size={12} /> Phone</label><input className="input" value={f.phone} onChange={up("phone")} placeholder="(555) 555-5555" /></div>
            <div style={{ flex: 1 }}><label className="field-label"><Icon name="mail" size={12} /> Email</label><input className="input" type="email" value={f.email} onChange={up("email")} placeholder="name@email.com" /></div>
          </div>
          <div><label className="field-label"><Icon name="mappin" size={12} /> Street address</label><input className="input" value={f.street} onChange={up("street")} placeholder="123 Main St" /></div>
          <div className="row gap-8">
            <div style={{ flex: 2 }}><label className="field-label">City</label><input className="input" value={f.city} onChange={up("city")} placeholder="Springfield" /></div>
            <div style={{ flex: 1 }}><label className="field-label">State</label><input className="input" value={f.state} placeholder="WI" maxLength={2} onChange={e => setF((s: any) => ({ ...s, state: e.target.value.toUpperCase() }))} /></div>
            <div style={{ flex: 1 }}><label className="field-label">ZIP</label><input className="input" value={f.zip} placeholder="12345" inputMode="numeric" onChange={up("zip")} /></div>
          </div>
        </div>
        <div className="row gap-8" style={{ marginTop: 16, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={!canSave} onClick={emit}><Icon name="check" size={13} />Save</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
function Customers({ store, go, params }: any) {
  const customers = store.customers || [];
  const [sel, setSel] = React.useState<any>(() => (params && params.focus) || (customers[0] && customers[0].id) || null);
  // on a phone the list and the customer are two screens, not two columns:
  // the list first, the customer on tap, Back returns to the list
  const narrow = useNarrowScreen(980);
  const [pane, setPane] = React.useState<any>(() => (params && params.focus ? "detail" : "list"));
  React.useEffect(() => { if (params && params.focus) setPane("detail"); }, [params && params.focus]);
  const [q, setQ] = React.useState<any>("");
  const [adding, setAdding] = React.useState(false);
  // possible-duplicate clusters (shared name / email / phone) + the one being merged
  const dupeGroups = React.useMemo(() => findDuplicateGroups(customers), [customers]);
  const dupeCount = dupeGroups.reduce((a, g) => a + g.length, 0);
  const [mergeGroup, setMergeGroup] = React.useState<any>(null);
  const [showDupes, setShowDupes] = React.useState(false);
  // active tag filter (click a tag → narrow the list to customers carrying it)
  const [tagFilter, setTagFilter] = React.useState<string>("");

  React.useEffect(() => { if (params && params.focus) setSel(params.focus); }, [params && params.focus]);

  // unread-message badges (red dot on rows whose hub thread has new messages)
  const [unreadCust, setUnreadCust] = React.useState<any>(new Set());
  React.useEffect(() => {
    const DB: any = window.VFP_DB;
    if (!(DB && DB.ready && DB.chat && DB.chat.unreadInfo)) return;
    let dead = false;
    const tick = () => DB.chat.unreadInfo().then((u: any) => { if (!dead && u) setUnreadCust(new Set(u.customers)); }).catch(() => {});
    tick();
    window.addEventListener("fw:unread", tick);
    return () => { dead = true; window.removeEventListener("fw:unread", tick); };
  }, []);

  const enriched = customers.map(c => {
    const j = jobsForCustomer(store, c);
    const wonTotal = j.projects.reduce((a, p) => a + (Number(p.total) || 0), 0) + j.looseJobs.reduce((a, w) => a + (Number(w.total) || 0), 0);
    const quotedTotal = j.quotes.reduce((a, x) => a + (Number(x.total) || 0), 0);
    // job count: projects + quotes that DIDN'T become one of those projects —
    // a won quote + its own project is ONE job, not two
    const looseQuotes = j.quotes.filter(x => !j.projects.some(p => p.fromQuote === x.id || (x.projectId && p.id === x.projectId)));
    return { ...c, _jobs: j, _won: wonTotal, _quoted: quotedTotal, _count: looseQuotes.length + j.projects.length + j.looseJobs.length };
  });
  // tag index: every tag in use across the book, with how many customers carry it
  const tagCounts: Record<string, number> = {};
  enriched.forEach(c => tagsOf(c).forEach(t => { tagCounts[t] = (tagCounts[t] || 0) + 1; }));
  const allTags = Object.keys(tagCounts).sort((a, b) => tagCounts[b] - tagCounts[a] || a.localeCompare(b));
  // a stale filter (last customer with that tag was untagged/deleted) just clears
  React.useEffect(() => { if (tagFilter && !tagCounts[tagFilter]) setTagFilter(""); }, [tagFilter, allTags.join("|")]);

  const byText = q ? enriched.filter(c => (c.name + " " + (c.email || "") + " " + (c.address || "")).toLowerCase().includes(q.toLowerCase())) : enriched;
  const filtered = tagFilter ? byText.filter(c => tagsOf(c).some(t => t.toLowerCase() === tagFilter.toLowerCase())) : byText;
  const current = enriched.find(c => c.id === sel) || filtered[0] || null;

  const totalPipeline = enriched.reduce((a, c) => a + c._quoted, 0);
  const totalWon = enriched.reduce((a, c) => a + c._won, 0);

  function addCustomer(data: any) {
    const id = newId("cust-");
    store.addCustomer({ id, name: (data.name || "").trim() || "New customer", phone: data.phone || "", email: data.email || "",
      address: data.address || "", street: data.street || "", city: data.city || "", state: data.state || "", zip: data.zip || "", notes: "" });
    setSel(id);
    setAdding(false);
  }

  return (
    <div className="page fade-up">
      <div className="row between" style={{ marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div><h1 className="display" style={{ fontSize: 28, margin: 0 }}>Customers</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>{customers.length} {customers.length === 1 ? "customer" : "customers"} · {CU_money0(totalWon)} won · {CU_money0(totalPipeline)} quoted</p></div>
        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
          <button className="btn btn-ghost" onClick={() => go("hub")} title="Every customer's private portal — quote, e-sign, pay, install tracker, messages. Preview it, copy links, switch it on or off."><Icon name="user" size={15} />Customer Hub</button>
          {customers.length > 0 && <button className="btn btn-ghost" onClick={() => downloadExport("customers", store)} title="Download every customer and lead as a CSV — lead source, tags and lifetime value included"><Icon name="download" size={15} />Export CSV</button>}
          <button className="btn btn-primary" onClick={() => setAdding(true)}><Icon name="plus" size={15} />Add Customer</button>
        </div>
      </div>
      {adding && <CustomerForm onSave={addCustomer} onClose={() => setAdding(false)} />}

      {/* possible-duplicate detection: a dismissible banner + an expandable list,
          each cluster offering a Merge action (with a survivor-pick + confirm). */}
      {dupeGroups.length > 0 && (
        <div className="card pad" style={{ marginBottom: 16, borderColor: "var(--amber-tint)", background: "var(--amber-tint)" }}>
          <div className="row between" style={{ flexWrap: "wrap", gap: 8 }}>
            <span className="row gap-8" style={{ alignItems: "center" }}>
              <Icon name="user" size={15} style={{ color: "var(--amber)" }} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>{dupeCount} possible duplicate{dupeCount === 1 ? "" : "s"}</span>
              <span className="muted" style={{ fontSize: 12.5 }}>in {dupeGroups.length} group{dupeGroups.length === 1 ? "" : "s"} — same name, email, or phone</span>
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowDupes(v => !v)}>{showDupes ? "Hide" : "Review & merge"}</button>
          </div>
          {showDupes && (
            <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
              {dupeGroups.map((g, i) => (
                <div key={i} className="row between" style={{ flexWrap: "wrap", gap: 8, padding: "10px 12px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.map((c: any) => c.name || "Unnamed").join("  ·  ")}</div>
                    <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{g.map((c: any) => c.email || c.phone || c.address || "no contact").join("  ·  ")}</div>
                  </div>
                  <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => setMergeGroup(g)}><Icon name="check" size={13} />Merge {g.length}</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {mergeGroup && <MergeModal group={mergeGroup} store={store} onClose={() => setMergeGroup(null)} onMerged={(keepId: string) => { setSel(keepId); setMergeGroup(null); }} />}

      {customers.length === 0
        ? <div className="card pad"><EmptyState icon="user" title="No customers yet" sub="Customers are created automatically when you send a quote or save an estimate — or add one manually." action={{ label: "Start an estimate", icon: "map", onClick: () => go("estimator") }} /></div>
        : <div className={"cust-layout" + (narrow ? (pane === "detail" ? " pane-detail" : " pane-list") : "")}>
            {/* list */}
            <aside className="cust-list card" style={{ padding: 0 }}>
              <div className="cust-search">
                <Icon name="search" size={15} className="muted" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers…" />
              </div>
              {/* tag filter: click a tag to narrow the list; "All" clears it */}
              {allTags.length > 0 && (
                <div className="row" style={{ gap: 6, flexWrap: "wrap", padding: "8px 12px 10px", borderBottom: "1px solid var(--line)" }}>
                  <button className={"filter-chip" + (tagFilter ? "" : " on")} style={{ fontSize: 11.5, padding: "3px 9px" }} onClick={() => setTagFilter("")}>All</button>
                  {allTags.map(t => (
                    <button key={t} className={"filter-chip" + (tagFilter === t ? " on" : "")} style={{ fontSize: 11.5, padding: "3px 9px" }}
                      onClick={() => setTagFilter(v => v === t ? "" : t)}>{t}<span className="faint" style={{ marginLeft: 5 }}>{tagCounts[t]}</span></button>
                  ))}
                </div>
              )}
              <div className="cust-rows">
                {filtered.length === 0
                  ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: "24px 12px" }}>No matches</p>
                  : filtered.map(c => (
                    <button key={c.id} className={"cust-row" + (current && c.id === current.id ? " on" : "")} onClick={() => { setSel(c.id); setPane("detail"); }}>
                      <div className="cust-ava" style={{ position: "relative" }}>
                        {(c.name || "?").trim().charAt(0).toUpperCase()}
                        {unreadCust.has(c.id) && <span style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, borderRadius: 99, background: "var(--red)", border: "2px solid var(--surface)" }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name || "Unnamed"}</div>
                        <div className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email || c.phone || c.address || "No contact"}</div>
                        {tagsOf(c).length > 0 && (
                          <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: "nowrap", overflow: "hidden" }}>
                            {tagsOf(c).slice(0, 3).map(t => (
                              <span key={t} style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 99, background: "var(--teal-tint)", color: "var(--teal)", whiteSpace: "nowrap" }}>{t}</span>
                            ))}
                            {tagsOf(c).length > 3 && <span className="faint" style={{ fontSize: 10, flexShrink: 0 }}>+{tagsOf(c).length - 3}</span>}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div className="tnum" style={{ fontSize: 13, fontWeight: 700, color: c._won ? "var(--teal)" : "var(--fg-3)" }}>{CU_money0(c._won || c._quoted)}</div>
                        <div className="faint" style={{ fontSize: 11 }}>{c._count} {c._count === 1 ? "job" : "jobs"}</div>
                      </div>
                    </button>
                  ))}
              </div>
            </aside>

            {/* detail */}
            {current && <CustomerDetail key={current.id} c={current} store={store} go={go} unread={unreadCust.has(current.id)} onBack={narrow ? () => setPane("list") : null} />}
          </div>}
    </div>
  );
}

/* Merge confirmation: pick which record survives, preview what carries over,
   then confirm. Merging is destructive (the others are removed), so it never
   fires on a single click. Each non-survivor is merged into the survivor via
   store.mergeCustomers, which re-points all of their jobs and unions fields. */
function MergeModal({ group, store, onClose, onMerged }: any) {
  // default survivor = the record with the most jobs, then the oldest (most
  // history / likely the "real" record). The rest fold into it.
  const counts: Record<string, number> = {};
  group.forEach((c: any) => { const j = jobsForCustomer(store, c); counts[c.id] = j.quotes.length + j.projects.length + j.workOrders.length; });
  const ranked = group.slice().sort((a: any, b: any) => (counts[b.id] - counts[a.id]) || ((a.createdAt || 0) - (b.createdAt || 0)));
  const [keepId, setKeepId] = React.useState<string>(ranked[0].id);
  const [confirming, setConfirming] = React.useState(false);
  const keep = group.find((c: any) => c.id === keepId);
  const drops = group.filter((c: any) => c.id !== keepId);
  const movingJobs = drops.reduce((a: number, c: any) => a + counts[c.id], 0);
  // which blank fields on the survivor will get filled from the dropped records
  const fillFields = ["phone", "email", "address", "street", "city", "state", "zip"].filter(
    (k) => !keep[k] && drops.some((d: any) => d[k]));

  function doMerge() {
    // fold every other record into the survivor; mergeCustomers re-points jobs
    // and unions fields, so order doesn't matter and nothing is orphaned.
    drops.forEach((d: any) => store.mergeCustomers(keepId, d.id));
    onMerged && onMerged(keepId);
  }

  return (
    <Portal>
    <div data-dialog onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,.55)", display: "grid", placeItems: "center", padding: 16 }}>
      <div className="card pad" onClick={(e: any) => e.stopPropagation()} style={{ maxWidth: 480, width: "100%" }}>
        <div className="kicker kicker-teal" style={{ marginBottom: 6 }}><Icon name="user" size={14} />Merge duplicates</div>
        <p className="muted" style={{ fontSize: 13, margin: "0 0 14px" }}>Keep one record. The other{drops.length === 1 ? "" : "s"} will be removed and everything on them — jobs, notes, files, contact info — moves to the one you keep.</p>
        <div className="field-label" style={{ marginBottom: 6 }}>Which record survives?</div>
        <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
          {group.map((c: any) => (
            <label key={c.id} className="row gap-8" style={{ alignItems: "center", padding: "10px 12px", border: "1px solid " + (c.id === keepId ? "var(--teal)" : "var(--line)"), borderRadius: 10, cursor: "pointer", background: c.id === keepId ? "var(--teal-tint)" : "var(--surface)" }}>
              <input type="radio" name="keep" checked={c.id === keepId} onChange={() => { setKeepId(c.id); setConfirming(false); }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5 }}>{c.name || "Unnamed"}</div>
                <div className="muted" style={{ fontSize: 12 }}>{[c.email, c.phone, c.address].filter(Boolean).join(" · ") || "No contact"}</div>
              </div>
              <span className="faint" style={{ fontSize: 11, flexShrink: 0 }}>{counts[c.id]} {counts[c.id] === 1 ? "job" : "jobs"}</span>
            </label>
          ))}
        </div>
        <div style={{ background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px", marginBottom: 14, fontSize: 12.5 }}>
          <div className="row gap-8" style={{ marginBottom: fillFields.length ? 4 : 0 }}><Icon name="check" size={13} style={{ color: "var(--teal)" }} /><span><b>{movingJobs}</b> job{movingJobs === 1 ? "" : "s"} re-pointed to <b>{keep.name || "this record"}</b></span></div>
          {fillFields.length > 0 && <div className="row gap-8"><Icon name="check" size={13} style={{ color: "var(--teal)" }} /><span>Fills its blank {fillFields.join(", ")}</span></div>}
        </div>
        <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          {confirming
            ? <button className="btn btn-sm" style={{ background: "var(--red)", color: "#fff" }} onClick={doMerge}><Icon name="check" size={13} />Confirm merge</button>
            : <button className="btn btn-primary btn-sm" onClick={() => setConfirming(true)}><Icon name="check" size={13} />Merge {group.length} into 1</button>}
        </div>
      </div>
    </div>
    </Portal>
  );
}

/* Tag chips + an inline add affordance for the customer detail header. Tags are
   stored on the customer (`tags: string[]`) via updateCustomer; suggestions
   that aren't already applied show in a small popover, and the operator can
   type a freeform tag too. */
/* ---- referral engine: per-customer share link + reward ledger --------------
   The link is the public estimator widget with ?ref=<this customer's hub
   token>; leads created from it carry referredBy, and each one that WINS owes
   the reward (settings.referralReward, $100 default) until marked paid
   (settings.referralPayouts). Happy customers become a channel you can see. */
function ReferralCard({ c, store }: any) {
  const [copied, setCopied] = React.useState<any>(false);
  const s = store.settings || {};
  const wtok = s.widget && s.widget.token;
  const reward = Number(s.referralReward) || 100;
  const payouts = s.referralPayouts || [];
  const referred = (store.quotes || []).filter((q: any) => q.referredBy && c.hubToken && q.referredBy === c.hubToken);
  const wonRef = referred.filter((q: any) => q.status === "signed" || q.status === "paid");
  const paidFor = (qid: string) => payouts.some((p: any) => p.quoteId === qid);
  const owed = wonRef.filter((q: any) => !paidFor(q.id));
  function copyLink() {
    let token = c.hubToken;
    if (!token) { token = genToken(); store.updateCustomer(c.id, { hubToken: token }); }
    const url = `${location.origin}/widget/${wtok}?ref=${token}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  }
  function markPaid(q: any) {
    store.setSettings({ referralPayouts: [...payouts, { id: "rp-" + Date.now(), customerId: c.id, quoteId: q.id, amount: reward, at: Date.now() }] });
    store.addActivity(`Referral reward $${reward} paid to ${c.name || "customer"} for ${q.customer || "a referral"}`);
  }
  if (!wtok && !referred.length) return null;   // widget not set up yet and nothing referred — stay quiet
  const statusCol: any = { pending: "var(--fg-3)", viewed: "var(--blue)", signed: "var(--violet)", paid: "var(--green)" };
  return (
    <div className="card pad" style={{ marginTop: 14 }}>
      <div className="row between" style={{ flexWrap: "wrap", gap: 8, marginBottom: referred.length ? 10 : 0 }}>
        <span className="row gap-8 kicker kicker-teal"><Icon name="send" size={13} />Referrals{owed.length > 0 && <span className="chip" style={{ fontSize: 10, color: "var(--amber)", borderColor: "var(--amber-tint)" }}>${reward * owed.length} reward owed</span>}</span>
        {wtok
          ? <button className="btn btn-outline btn-sm" onClick={copyLink} title={`Copy ${c.name || "this customer"}'s personal referral link — leads from it credit them for the $${reward} reward`}>
              <Icon name={copied ? "check" : "send"} size={12} />{copied ? "Link copied ✓" : "Copy referral link"}
            </button>
          : <span className="faint" style={{ fontSize: 11 }}>Set up the Estimator Widget to generate links</span>}
      </div>
      {referred.length === 0
        ? <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>Send them their link after the install ("know anyone who needs a fence? ${reward} on us") — leads from it show up here with the reward tracked automatically.</p>
        : <div style={{ display: "grid", gap: 6 }}>
          {referred.map((q: any) => (
            <div key={q.id} className="ready-row">
              <span className="chip" style={{ fontSize: 10, color: statusCol[q.status] || "var(--fg-3)", borderColor: "var(--line-2)", textTransform: "capitalize", flexShrink: 0 }}>{q.status}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.customer || "Lead"}{q.total ? <span className="muted" style={{ fontWeight: 500 }}> · {CU_money0(q.total)}</span> : null}</span>
              {(q.status === "signed" || q.status === "paid") && (paidFor(q.id)
                ? <span className="chip green" style={{ fontSize: 10, flexShrink: 0 }}>${reward} paid ✓</span>
                : <button className="btn btn-outline btn-sm" style={{ flexShrink: 0 }} onClick={() => markPaid(q)} title={`Record the $${reward} referral reward as paid`}>Pay ${reward} reward</button>)}
            </div>
          ))}
        </div>}
    </div>
  );
}

function CustomerTags({ c, store }: any) {
  const tags = tagsOf(c);
  const set = (next: string[]) => store.updateCustomer(c.id, { tags: next });
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const boxRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: any) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  function commit(raw: string) {
    const next = addTag(tags, raw);
    if (next !== tags) set(next);
    setDraft(""); setOpen(false);
  }
  const suggestions = SUGGESTED_TAGS.filter(s => !tags.some(t => t.toLowerCase() === s.toLowerCase()));
  return (
    <div className="row" style={{ gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 7 }}>
      {tags.map(t => (
        <span key={t} className="chip teal" style={{ fontSize: 11, padding: "2px 6px 2px 10px", gap: 4 }}>
          {t}
          <button onClick={() => set(removeTag(tags, t))} aria-label={"Remove tag " + t}
            style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit", opacity: 0.7, display: "grid", placeItems: "center" }}>
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      <div ref={boxRef} style={{ position: "relative" }}>
        <button className="chip" style={{ fontSize: 11, padding: "2px 9px", cursor: "pointer", borderStyle: "dashed" }} onClick={() => setOpen(v => !v)}>
          <Icon name="plus" size={11} />{tags.length ? "Tag" : "Add tag"}
        </button>
        {open && (
          <div className="card cust-tag-pop" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, padding: 10, width: 220, boxShadow: "var(--shadow-2, 0 8px 24px rgba(0,0,0,.18))" }}>
            <input className="input" autoFocus value={draft} placeholder="New tag…" style={{ fontSize: 13, marginBottom: suggestions.length ? 8 : 0 }}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") commit(draft); if (e.key === "Escape") setOpen(false); }} />
            {suggestions.length > 0 && (
              <div className="row" style={{ gap: 5, flexWrap: "wrap" }}>
                {suggestions.map(s => (
                  <button key={s} className="filter-chip" style={{ fontSize: 11, padding: "3px 9px" }} onClick={() => commit(s)}>{s}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerDetail({ c, store, go, unread, onBack }: any) {
  const set = (patch) => store.updateCustomer(c.id, patch);
  const [editing, setEditing] = React.useState(false);
  const { quotes, projects, workOrders } = c._jobs;
  const statusColor = { pending: "var(--fg-3)", viewed: "var(--blue)", signed: "var(--violet)", paid: "var(--green)" };
  const [tab, setTab] = React.useState<any>("overview");

  // open balance across won quotes — subtract the FULL payment ledger
  // (manual cash/check entries + Stripe), not just the webhook depositPaid;
  // a $10k cash payment was reading as still-owed before.
  const balance = quotes.filter(q => q.status === "paid" || q.status === "signed")
    .reduce((a, q) => a + Math.max(0, (q.total || 0) - quotePayments(q).paid), 0);
  const openTasks = (c.tasks || []).filter((t: any) => !t.done);
  // needs-attention items with $ value (Jobber-style)
  const attention = [];
  if (unread) attention.push({ icon: "mail", label: "New message — reply needed", val: 0, go: () => setTab("messages") });
  quotes.filter(q => q.status === "pending" || q.status === "viewed").forEach(q => attention.push({ icon: "send", label: "Quote awaiting response", val: q.total, go: () => go("quote", { quoteId: q.id }) }));
  workOrders.filter(w => w.status === "materials").forEach(w => attention.push({ icon: "box", label: "Supplies to order", val: w.total, go: () => go("workorders") }));
  workOrders.filter(w => w.status === "unscheduled").forEach(w => attention.push({ icon: "calendar", label: "Job needs scheduling", val: w.total, go: () => go("workorders") }));
  if (balance > 0) attention.push({ icon: "dollar", label: "Balance due", val: balance, go: () => go("quote", { quoteId: (quotes.find(q => q.status === "paid" || q.status === "signed") || {}).id }) });
  // open follow-ups (e.g. "quote them for a gate") surface alongside money items
  openTasks.forEach((t: any) => attention.push({ icon: "clipboard", label: "Follow-up: " + t.text, val: 0, due: t.due, go: () => setTab("notes") }));
  const attnWorth = attention.reduce((a, x) => a + (x.val || 0), 0);

  function newEstimate() { go("estimator", { prefillCustomer: c.id }); }
  function message() { go("messages", { customerId: c.id }); }

  const TABS: any = [["overview", "Overview"], ["jobs", "Jobs"], ["timeline", "Timeline"], ["property", "Property"], ["notes", "Notes", openTasks.length], ["billing", "Billing"], ["messages", "Messages", unread]];

  return (
    <section className="cust-detail">
      {onBack && <button className="btn btn-ghost btn-sm pane-back" onClick={onBack}><Icon name="chevright" size={14} style={{ transform: "rotate(180deg)" }} />All customers</button>}
      {/* ---- profile header: LTV + quick actions ---- */}
      <div className="card pad">
        <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
          <div className="row gap-12" style={{ minWidth: 0 }}>
            <div className="cust-ava lg">{(c.name || "?").trim().charAt(0).toUpperCase()}</div>
            <div style={{ minWidth: 0 }}>
              <NameInput value={c.name || ""} onCommit={(v: string) => { if (v !== (c.name || "")) set({ name: v }); }} />
              <div className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.email || c.phone || "No contact"} · since {new Date(customerSinceMs(c)).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</div>
              <CustomerTags c={c} store={store} />
            </div>
          </div>
          <div className="row gap-8 cust-detail-actions" style={{ flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-sm" onClick={newEstimate}><Icon name="plus" size={13} />New estimate</button>
            <HubLinkButton store={store} customerId={c.id} />
            <HubRegenButton store={store} customerId={c.id} />
            <button className="btn btn-ghost btn-sm" onClick={message}><Icon name="chat" size={13} />Message</button>
            {(c.phone || c.email) && (
              <details className="act-menu">
                <summary className="btn btn-ghost btn-sm" aria-label="Contact options"><Icon name="phone" size={13} />Contact<Icon name="chevdown" size={12} /></summary>
                <div className="act-menu-pop" role="menu">
                  {c.phone && <a role="menuitem" href={"tel:" + c.phone}><Icon name="phone" size={14} />Call {c.phone}</a>}
                  {c.phone && <a role="menuitem" href={"sms:" + c.phone}><Icon name="chat" size={14} />Text {c.phone}</a>}
                  {c.email && <a role="menuitem" href={"mailto:" + c.email}><Icon name="mail" size={14} />Email {c.email}</a>}
                </div>
              </details>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}><Icon name="edit" size={13} />Edit</button>
          </div>
          {editing && <CustomerForm initial={c} onSave={(data: any) => { set({ name: data.name, phone: data.phone, email: data.email, address: data.address, street: data.street, city: data.city, state: data.state, zip: data.zip }); setEditing(false); }} onClose={() => setEditing(false)} />}
        </div>
        <div className="cust-ltv">
          <div><div className="muted" style={{ fontSize: 11.5 }}>Lifetime won</div><div className="display tnum" style={{ fontSize: 22, color: "var(--teal)" }}>{CU_money0(c._won)}</div></div>
          <div><div className="muted" style={{ fontSize: 11.5 }}>Quoted</div><div className="display tnum" style={{ fontSize: 22 }}>{CU_money0(c._quoted)}</div></div>
          <div><div className="muted" style={{ fontSize: 11.5 }}>Open balance</div><div className="display tnum" style={{ fontSize: 22, color: balance > 0 ? "var(--amber)" : "var(--fg-3)" }}>{CU_money0(balance)}</div></div>
          <div><div className="muted" style={{ fontSize: 11.5 }}>Jobs</div><div className="display tnum" style={{ fontSize: 22 }}>{c._count}</div></div>
        </div>
      </div>

      {/* ---- needs attention feed ---- */}
      {attention.length > 0 && (
        <div className="card pad">
          <div className="row between" style={{ marginBottom: 10 }}><span className="kicker kicker-teal"><Icon name="bolt" size={13} /> Needs attention</span><span className="muted" style={{ fontSize: 12 }}>{attention.length} item{attention.length > 1 ? "s" : ""} · {CU_money0(attnWorth)}</span></div>
          <div style={{ display: "grid", gap: 7 }}>
            {attention.map((a, i) => (
              <button key={i} className="attn-row" onClick={a.go}>
                <span className="attn-ic"><Icon name={a.icon} size={14} /></span>
                <span style={{ flex: 1, textAlign: "left", fontSize: 13.5, fontWeight: 600 }}>{a.label}</span>
                {a.val ? <span className="tnum" style={{ color: "var(--teal)", fontWeight: 700 }}>{CU_money0(a.val)}</span> : null}
                <Icon name="chevright" size={14} className="muted" />
              </button>
            ))}
          </div>
        </div>
      )}

      <ReferralCard c={c} store={store} />

      {/* ---- tabs ---- */}
      <div className="cat-tabs" style={{ margin: "2px 0 0" }}>
        {TABS.map(([id, lb, badge]) => <button key={id} className={"filter-chip" + (tab === id ? " on" : "")} onClick={() => setTab(id)}>{lb}{badge ? <span className="chip-badge">{typeof badge === "number" ? badge : 1}</span> : null}</button>)}
      </div>

      {tab === "timeline" && <CustomerTimeline c={c} jobs={c._jobs} go={go} setTab={setTab} />}

      {(tab === "overview" || tab === "jobs") && (
        <>
          {quotes.length > 0 && (
            <div className="card pad">
              <div className="kicker" style={{ marginBottom: 12 }}>Quotes</div>
              <div style={{ display: "grid", gap: 8 }}>
                {quotes.slice().reverse().map(qq => (
                  <div key={qq.id} className="proj-row" {...clickable(() => go("quote", { quoteId: qq.id }), { label: `Open the ${qq.productName || "estimate"} quote` })}>
                    <div style={{ flex: 1 }}><div className="row gap-6" style={{ fontWeight: 600, fontSize: 13.5, flexWrap: "wrap" }}>{qq.productName}<VerticalChip record={qq} /></div><div className="muted" style={{ fontSize: 12 }}>{fmtMeasure(qq)} · {new Date(qq.createdAt).toLocaleDateString()}</div></div>
                    <span className="chip" style={{ textTransform: "capitalize", color: statusColor[qq.status] || "var(--fg-3)", borderColor: "var(--line-2)" }}>{qq.status}</span>
                    <div className="display tnum" style={{ fontSize: 15, color: "var(--teal)" }}>{CU_money0(qq.total)}</div>
                    <Icon name="chevright" size={15} className="muted" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* the care plans this customer's concrete is on — booked in one tap (§13 item 15) */}
          <CarePlansCard c={c} store={store} go={go} />
          {/* review + referral + warranty for every finished job — the
              automatic emails, previewable, and where they stand */}
          {workOrders.filter((w: any) => w.status === "completed").length > 0 && (
            <div className="card pad" data-tour="cust-followup">
              <div className="row between" style={{ marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <span className="kicker kicker-teal"><Icon name="send" size={13} /> After the job — review &amp; referral emails</span>
                <button className="btn btn-ghost btn-sm" onClick={() => go("sequences", { seq: "seq-review" })}><Icon name="edit" size={12} />Edit the emails</button>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {workOrders.filter((w: any) => w.status === "completed").map((w: any) => (
                  <div key={w.id}>
                    {workOrders.filter((x: any) => x.status === "completed").length > 1 && <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>{w.title || w.customer}</div>}
                    <FollowUp wo={w} store={store} set={(patch: any) => store.updateWorkOrder(w.id, patch)} go={go} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {workOrders.length > 0 && (
            <div className="card pad">
              <div className="kicker" style={{ marginBottom: 12 }}>Work Orders</div>
              <div style={{ display: "grid", gap: 8 }}>
                {workOrders.map(w => (
                  <div key={w.id} className="proj-row" {...clickable(() => go("workorders"), { label: "Open work orders" })}>
                    <div style={{ flex: 1 }}><div style={{ fontWeight: 600, fontSize: 13.5 }}>{w.title}</div><div className="muted" style={{ fontSize: 12 }}>{w.scheduledStart || "Unscheduled"}</div></div>
                    <span className="chip" style={{ textTransform: "capitalize", borderColor: "var(--line-2)" }}>{(w.status || "").replace("_", " ")}</span>
                    <div className="display tnum" style={{ fontSize: 15, color: "var(--teal)" }}>{CU_money0(w.total || 0)}</div>
                    <Icon name="chevright" size={15} className="muted" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {quotes.length === 0 && workOrders.length === 0 && <div className="card pad"><EmptyState icon="clipboard" title="No jobs yet" sub="Quotes and work orders appear here." /></div>}
        </>
      )}

      {tab === "property" && (() => {
        // structured parts, seeding `street` from a legacy single-line address
        const hasParts = !!(c.street || c.city || c.state || c.zip);
        const addr = { street: c.street || (!hasParts ? (c.address || "") : ""), city: c.city || "", state: c.state || "", zip: c.zip || "" };
        // write a part + recompose the single-line `address` (the source of truth)
        const setAddr = (patch: any) => { const next = { ...addr, ...patch }; set({ ...patch, address: composeAddress(next) || c.address || "" }); };
        return (
        <div className="card pad cust-fields">
          <div style={{ gridColumn: "1 / -1" }}><label className="field-label"><Icon name="mappin" size={12} /> Street address</label><input className="input" value={addr.street} onChange={e => setAddr({ street: e.target.value })} placeholder="123 Main St" /></div>
          <div style={{ gridColumn: "1 / -1" }} className="row gap-8">
            <div style={{ flex: 2 }}><label className="field-label">City</label><input className="input" value={addr.city} onChange={e => setAddr({ city: e.target.value })} placeholder="Springfield" /></div>
            <div style={{ flex: 1 }}><label className="field-label">State</label><input className="input" value={addr.state} placeholder="WI" maxLength={2} onChange={e => setAddr({ state: e.target.value.toUpperCase() })} /></div>
            <div style={{ flex: 1 }}><label className="field-label">ZIP</label><input className="input" value={addr.zip} placeholder="12345" inputMode="numeric" onChange={e => setAddr({ zip: e.target.value })} /></div>
          </div>
          <div><label className="field-label">Gate / access code</label><input className="input" value={c.gateCode || ""} onChange={e => set({ gateCode: e.target.value })} placeholder="e.g. #1234" /></div>
          <div><label className="field-label">Pets on site</label><input className="input" value={c.pets || ""} onChange={e => set({ pets: e.target.value })} placeholder="e.g. dog in back yard" /></div>
          <div style={{ gridColumn: "1 / -1" }}><label className="field-label"><Icon name="edit" size={12} /> Site notes</label><textarea className="textarea" value={c.notes || ""} onChange={e => set({ notes: e.target.value })} placeholder="Access, slope, preferred contact time…" /></div>
        </div>
        );
      })()}

      {tab === "notes" && <CustomerWorkspace c={c} store={store} />}

      {tab === "billing" && (
        <div className="card pad cust-fields">
          <div><label className="field-label"><Icon name="phone" size={12} /> Phone</label><input className="input" value={c.phone || ""} onChange={e => set({ phone: e.target.value })} /></div>
          <div><label className="field-label"><Icon name="mail" size={12} /> Email</label><input className="input" value={c.email || ""} onChange={e => set({ email: e.target.value })} /></div>
          <div style={{ gridColumn: "1 / -1", marginTop: 4 }}>
            <div className="row between" style={{ padding: "8px 0", borderBottom: "1px solid var(--line)" }}><span className="muted" style={{ fontSize: 13 }}>Lifetime won</span><span className="tnum" style={{ fontWeight: 700 }}>{CU_money0(c._won)}</span></div>
            <div className="row between" style={{ padding: "8px 0" }}><span className="muted" style={{ fontSize: 13 }}>Open balance</span><span className="tnum" style={{ fontWeight: 700, color: balance > 0 ? "var(--amber)" : "var(--fg-3)" }}>{CU_money0(balance)}</span></div>
          </div>
        </div>
      )}

      {tab === "messages" && <CustomerThreadPeek customer={c} onOpen={message} />}

      <button className="btn btn-sm" style={{ background: "var(--red-tint)", color: "var(--red)", justifySelf: "start" }} onClick={async () => { if (await confirmDialog({ title: "Delete this customer?", body: "Their quotes, jobs and work orders are kept (unlinked, with the name and contact details still on them).", confirmLabel: "Delete customer", danger: true })) store.deleteCustomer(c.id); }}><Icon name="trash" size={13} />Delete customer</button>
    </section>
  );
}

/* ============================================================
   Customer timeline — every quote/job/payment/note merged into one
   reverse-chronological activity feed (the "CRM history" view). Pure
   read: buildTimeline derives it from the existing rollup + noteLog/
   tasks/payments, so it always reflects current state with nothing extra
   persisted. Events are grouped under day headers; each row deep-links
   to its record where one exists.
   ============================================================ */
function CustomerTimeline({ c, jobs, go, setTab }: any) {
  const events = React.useMemo(() => buildTimeline(c, jobs, go, setTab), [c, jobs]);
  if (events.length === 0) return <div className="card pad"><EmptyState icon="activity" title="No activity yet" sub="Quotes, jobs, payments, notes and follow-ups all show up here as they happen." /></div>;
  // group consecutive events by calendar day for a scannable, dated feed
  const dayKey = (ts: number) => new Date(ts).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const groups: { day: string; items: any[] }[] = [];
  events.forEach(e => {
    const d = dayKey(e.ts);
    const last = groups[groups.length - 1];
    if (last && last.day === d) last.items.push(e); else groups.push({ day: d, items: [e] });
  });
  return (
    <div className="card pad">
      <div className="row between" style={{ marginBottom: 12 }}>
        <span className="kicker kicker-teal"><Icon name="activity" size={13} /> Activity</span>
        <span className="muted" style={{ fontSize: 12 }}>{events.length} event{events.length === 1 ? "" : "s"}</span>
      </div>
      <div style={{ display: "grid", gap: 14 }}>
        {groups.map((g, gi) => (
          <div key={gi}>
            <div className="faint" style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 7 }}>{g.day}</div>
            <div style={{ display: "grid", gap: 7 }}>
              {g.items.map((e, i) => {
                const inner = (
                  <>
                    <span className="attn-ic"><Icon name={e.icon} size={14} /></span>
                    <span style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.label}</span>
                      {e.sub ? <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{e.sub}</span> : null}
                    </span>
                    {e.val ? <span className="tnum" style={{ color: "var(--teal)", fontWeight: 700, flexShrink: 0 }}>{CU_money0(e.val)}</span> : null}
                    {e.go ? <Icon name="chevright" size={14} className="muted" /> : null}
                  </>
                );
                return e.go
                  ? <button key={i} className="attn-row" onClick={e.go}>{inner}</button>
                  : <div key={i} className="attn-row" style={{ cursor: "default" }}>{inner}</div>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Customer workspace — the CRM brain for one customer:
   follow-up tasks, a running notes log, and photos / files
   (each with its own caption). All of it lives on the customer
   record, so it syncs to the cloud with the rest of the state.
   ============================================================ */
function CW_when(ts: number) {
  if (!ts) return "";
  const d = new Date(ts), now = Date.now(), diff = now - ts;
  if (diff < 60e3) return "just now";
  if (diff < 36e5) return Math.round(diff / 60e3) + "m ago";
  if (diff < 864e5 && d.getDate() === new Date().getDate()) return "today " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + (now - ts > 31536e6 ? " " + d.getFullYear() : "");
}
const CW_uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

/* Care plans (plan §13 item 15; lib/careplans.ts). A finished seal / caulk /
   cleaning job started these (app.tsx); the owner can add one by hand (a
   re-seal offered after a lift). Each row: what and how often, when it's due,
   and the moves — Book it (a fresh concrete estimate from the plan's own
   scope, priced off today's rate cards, tied back to the plan), Remind (the
   service's care_reminder, or a text / email), pause, remove. Shown only
   where concrete exists for this account or this customer. */
function CarePlansCard({ c, store, go }: any) {
  const plans: any[] = Array.isArray(c.carePlans) ? c.carePlans : [];
  const today = localYmd(new Date());
  const cxHere = (store.enabledLenses || []).includes("concrete") || plans.length > 0 || ((c._jobs && c._jobs.quotes) || []).some((q: any) => verticalOf(q) === "concrete");
  const [adding, setAdding] = React.useState<any>(null);   // { jobType, lastDone, every, sqft, ft }
  if (!cxHere) return null;
  const save = (list: any[]) => store.updateCustomer(c.id, { carePlans: list });
  const patchPlan = (id: string, patch: any) => save(plans.map((p: any) => (p.id === id ? { ...p, ...patch } : p)));
  function book(p: any) {
    const base: any = buildConcreteQuote({ blocks: rebookBlocks(p), scopeNotes: `${CARE_LABEL[p.jobType] || "Care visit"} from the care plan${p.lastDone ? " — last done " + p.lastDone : ""}` }, store.settings || {},
      { customer: c.name || "", phone: c.phone || "", email: c.email || "", address: c.address || "" });
    const { result: _r, ...rec } = base;
    const q: any = { id: newId("q"), status: "draft", createdAt: Date.now(), source: "careplan", customerId: c.id, carePlanId: p.id, ...rec };
    if (!store.addQuote(q)) return;
    patchPlan(p.id, { lastQuoteId: q.id });
    store.addActivity(`${CARE_LABEL[p.jobType] || "Care"} estimate for ${c.name || "customer"} — from their care plan`);
    go("quote", { quoteId: q.id });
  }
  async function remind(p: any) {
    const surf = p.scope && Array.isArray(p.scope.surfaces) && p.scope.surfaces.length ? p.scope.surfaces.join(" and ").replace(/-/g, " ") : "your concrete";
    const vars = { since: sinceLabel(p, today), did: (CARE_VERB[p.jobType] || "worked on") + " " + surf, work: p.jobType === "seal" ? "seal coat" : p.jobType === "caulk" ? "round of caulking" : "cleaning", address: c.address || "your place" };
    const mark = () => patchPlan(p.id, { remindedFor: p.nextDue });
    const DB: any = (window as any).VFP_DB;
    if (DB && DB.messageSendNow && DB.apiBase && DB.apiBase()) {
      try {
        const r = await DB.messageSendNow({ kind: "care_reminder", customerId: c.id, vertical: "concrete", vars });
        notify(r && r.deferred ? "Queued — it goes out when quiet hours lift." : "Reminder sent.");
        mark(); return;
      } catch (e: any) { notify(`Couldn't send it (${String((e && e.message) || e)}) — opening a message instead.`, { kind: "error" }); }
    }
    const body = `Hi ${String(c.name || "there").split(/\s+/)[0]}, it's been about ${vars.since} since we ${vars.did} at ${vars.address}. A fresh ${vars.work} keeps it protected — want us to put you on the schedule?`;
    if (c.phone) window.open("sms:" + String(c.phone).replace(/[^+\d]/g, "") + "?&body=" + encodeURIComponent(body), "_blank");
    else if (c.email) window.open(`mailto:${c.email}?subject=${encodeURIComponent("Time for a fresh " + vars.work + "?")}&body=${encodeURIComponent(body)}`, "_blank");
    else { notify("No phone or email on this customer.", { kind: "error" }); return; }
    mark();
  }
  function addPlan() {
    const a = adding; if (!a || !a.jobType) return;
    const every = Math.max(1, Math.round(Number(a.every) || CARE_EVERY[a.jobType]));
    const lastDone = /^\d{4}-\d{2}-\d{2}$/.test(a.lastDone || "") ? a.lastDone : today;
    const scope: any = a.jobType === "caulk" ? { ft: Math.max(0, Number(a.ft) || 0) } : { sqft: Math.max(0, Number(a.sqft) || 0) };
    save([...plans, { id: "cp-m-" + newId("x"), vertical: "concrete", jobType: a.jobType, everyMonths: every, nextDue: addMonths(lastDone, every), lastDone, sourceQuoteId: null, sourceWoId: null, lastQuoteId: null, scope, status: "active", createdAt: Date.now(), remindedFor: null }]);
    setAdding(null);
  }
  const fmt = (ymd: string) => new Date(ymd + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <div className="card pad" data-tour="cust-careplans">
      <div className="row between" style={{ marginBottom: plans.length || adding ? 10 : 0, flexWrap: "wrap", gap: 8 }}>
        <span className="kicker kicker-teal"><Icon name="calendar" size={13} /> Care plans</span>
        <div className="row gap-8">
          {plans.length === 0 && !adding && <span className="muted" style={{ fontSize: 12 }}>A finished seal, caulk or cleaning job starts one here.</span>}
          {!adding && <button className="btn btn-ghost btn-sm" onClick={() => setAdding({ jobType: "seal", lastDone: today, every: CARE_EVERY.seal, sqft: "", ft: "" })}><Icon name="plus" size={12} />Add a plan</button>}
        </div>
      </div>
      {adding && (
        <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", marginBottom: plans.length ? 10 : 0 }}>
          <select className="input" style={{ width: 130 }} value={adding.jobType} onChange={e => setAdding({ ...adding, jobType: e.target.value, every: CARE_EVERY[e.target.value] || 12 })}>
            {CARE_TYPES.map(t => <option key={t} value={t}>{CARE_LABEL[t]}</option>)}
          </select>
          {adding.jobType === "caulk"
            ? <input className="input tnum" style={{ width: 110 }} type="number" min={0} placeholder="ft of joints" value={adding.ft} onChange={e => setAdding({ ...adding, ft: e.target.value })} />
            : <input className="input tnum" style={{ width: 110 }} type="number" min={0} placeholder="sq ft" value={adding.sqft} onChange={e => setAdding({ ...adding, sqft: e.target.value })} />}
          <label className="muted" style={{ fontSize: 12 }}>last done <input className="input" type="date" style={{ width: 150 }} value={adding.lastDone} onChange={e => setAdding({ ...adding, lastDone: e.target.value })} /></label>
          <label className="muted" style={{ fontSize: 12 }}>every <input className="input tnum" type="number" min={1} max={60} style={{ width: 64 }} value={adding.every} onChange={e => setAdding({ ...adding, every: e.target.value })} /> months</label>
          <button className="btn btn-primary btn-sm" onClick={addPlan}><Icon name="check" size={12} />Add</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setAdding(null)}>Cancel</button>
        </div>
      )}
      {plans.length > 0 && (
        <div style={{ display: "grid", gap: 7 }}>
          {plans.slice().sort((a: any, b: any) => String(a.nextDue).localeCompare(String(b.nextDue))).map((p: any) => {
            const d = planDue(p, today, 45);
            const booked = !!p.lastQuoteId && (store.allQuotes || store.quotes || []).some((q: any) => q && q.id === p.lastQuoteId);
            const reminded = p.remindedFor && p.remindedFor === p.nextDue;
            return (
              <div key={p.id} className={"care-row" + (d.due && !booked ? " due" : "") + (p.status === "paused" ? " paused" : "")}>
                <div className="care-main">
                  <div style={{ fontWeight: 600 }}>{planLabel(p)}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {p.status === "paused" ? "Paused" : booked ? "Estimate drafted" : d.due ? (d.days < 0 ? `Overdue by ${-d.days}d` : d.days === 0 ? "Due today" : `Due in ${d.days}d`) : `Next ${fmt(p.nextDue)}`}
                    {p.lastDone ? ` · last done ${fmt(p.lastDone)}` : ""}{reminded ? " · reminded" : ""}
                  </div>
                </div>
                <div className="row gap-6" style={{ flexWrap: "wrap" }}>
                  {booked
                    ? <button className="btn btn-outline btn-sm" onClick={() => go("quote", { quoteId: p.lastQuoteId })}><Icon name="clipboard" size={12} />Open estimate</button>
                    : p.status === "active" && <button className="btn btn-primary btn-sm" onClick={() => book(p)} title="A fresh estimate from the plan's own scope, priced off today's rate cards"><Icon name="plus" size={12} />Book it</button>}
                  {p.status === "active" && !booked && d.due && !reminded && (c.phone || c.email) && <button className="btn btn-ghost btn-sm" onClick={() => remind(p)} title="Text or email: it's been about two years since we sealed your driveway…"><Icon name="send" size={12} />Remind</button>}
                  <button className="btn btn-ghost btn-sm" onClick={() => patchPlan(p.id, { status: p.status === "paused" ? "active" : "paused" })}>{p.status === "paused" ? "Resume" : "Pause"}</button>
                  <button className="proj-del" style={{ background: "none", border: 0, color: "var(--fg-3)", cursor: "pointer" }} aria-label="Remove plan" onClick={async () => { if (await confirmDialog({ title: "Remove this care plan?", body: planLabel(p) + " — the customer's page stops showing it.", confirmLabel: "Remove", danger: true })) save(plans.filter((x: any) => x.id !== p.id)); }}><Icon name="trash" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CustomerWorkspace({ c, store }: any) {
  const tasks: any[] = c.tasks || [];
  const notes: any[] = c.noteLog || [];
  const files: any[] = c.files || [];
  const save = (patch: any) => store.updateCustomer(c.id, patch);

  // ---- tasks / follow-ups ----
  const [taskText, setTaskText] = React.useState("");
  const [taskDue, setTaskDue] = React.useState("");
  function addTask() {
    const t = taskText.trim(); if (!t) return;
    save({ tasks: [...tasks, { id: CW_uid(), text: t, done: false, due: taskDue || null, createdAt: Date.now(), doneAt: null }] });
    setTaskText(""); setTaskDue("");
  }
  function toggleTask(id: string) {
    save({ tasks: tasks.map(t => t.id === id ? { ...t, done: !t.done, doneAt: !t.done ? Date.now() : null } : t) });
  }
  function delTask(id: string) { save({ tasks: tasks.filter(t => t.id !== id) }); }
  const sortedTasks = tasks.slice().sort((a, b) =>
    (a.done ? 1 : 0) - (b.done ? 1 : 0) || (a.due ? Date.parse(a.due) : Infinity) - (b.due ? Date.parse(b.due) : Infinity) || a.createdAt - b.createdAt);
  const today = localYmd();

  // ---- notes ----
  const [noteText, setNoteText] = React.useState("");
  function addNote() {
    const t = noteText.trim(); if (!t) return;
    save({ noteLog: [{ id: CW_uid(), text: t, createdAt: Date.now() }, ...notes] });
    setNoteText("");
  }
  function delNote(id: string) { save({ noteLog: notes.filter(n => n.id !== id) }); }

  // ---- files & photos ----
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files && e.target.files[0]; e.currentTarget.value = "";
    if (!f) return;
    setBusy(true);
    try {
      let url: string, kind: "image" | "file";
      if (f.type.startsWith("image/")) { url = await downscaleToDataUrl(f); kind = "image"; }
      else {
        if (f.size > 3 * 1024 * 1024) { notify("That file is over 3 MB — please attach a smaller PDF or a photo of it."); setBusy(false); return; }
        url = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(f); });
        kind = "file";
      }
      save({ files: [{ id: CW_uid(), name: f.name, kind, url, caption: "", createdAt: Date.now() }, ...files] });
    } catch { notify("Couldn't attach that file."); }
    finally { setBusy(false); }
  }
  function setCaption(id: string, caption: string) { save({ files: files.map(f => f.id === id ? { ...f, caption } : f) }); }
  function delFile(id: string) { save({ files: files.filter(f => f.id !== id) }); }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {/* follow-ups */}
      <div className="card pad">
        <div className="row between" style={{ marginBottom: 12 }}>
          <span className="kicker kicker-teal"><Icon name="clipboard" size={13} /> Follow-ups</span>
          {tasks.length > 0 && <span className="muted" style={{ fontSize: 12 }}>{tasks.filter(t => !t.done).length} open</span>}
        </div>
        <div className="row gap-8" style={{ marginBottom: tasks.length ? 12 : 0, flexWrap: "wrap" }}>
          <input className="input" style={{ flex: "1 1 180px", minWidth: 0 }} value={taskText} placeholder="e.g. Quote them for a gate on the south side"
            onChange={e => setTaskText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addTask(); }} />
          <input className="input" type="date" style={{ width: 150 }} value={taskDue} min={today} onChange={e => setTaskDue(e.target.value)} title="Optional due date" />
          <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={addTask} disabled={!taskText.trim()}><Icon name="plus" size={13} />Add</button>
        </div>
        {tasks.length > 0 && (
          <div style={{ display: "grid", gap: 7 }}>
            {sortedTasks.map(t => {
              const overdue = !t.done && t.due && t.due < today;
              return (
                <div key={t.id} className={"ready-row" + (t.done ? " on" : "")}>
                  <button className={"ready-check" + (t.done ? " on" : "")} onClick={() => toggleTask(t.id)} aria-label={t.done ? "Mark not done" : "Mark done"}>{t.done && <Icon name="check" size={12} />}</button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, textDecoration: t.done ? "line-through" : "none", opacity: t.done ? 0.6 : 1 }}>{t.text}</div>
                    {t.due && <div style={{ fontSize: 11.5, color: overdue ? "var(--red)" : "var(--fg-3)" }}><Icon name="calendar" size={11} style={{ verticalAlign: "-1px" }} /> {overdue ? "Overdue · " : "Due "}{new Date(t.due + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" })}</div>}
                  </div>
                  <button className="proj-del" style={{ background: "none", border: 0, color: "var(--fg-3)", cursor: "pointer", flexShrink: 0 }} onClick={() => delTask(t.id)} aria-label="Delete task"><Icon name="trash" size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* notes log */}
      <div className="card pad">
        <div className="kicker kicker-teal" style={{ marginBottom: 12 }}><Icon name="edit" size={13} /> Notes</div>
        <div style={{ marginBottom: notes.length ? 14 : 0 }}>
          <textarea className="textarea" value={noteText} placeholder="Log a call, a site observation, what they asked for…"
            onChange={e => setNoteText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }} style={{ minHeight: 64 }} />
          <div className="row between" style={{ marginTop: 8 }}>
            <span className="faint" style={{ fontSize: 11 }}>⌘/Ctrl + Enter to save</span>
            <button className="btn btn-primary btn-sm" onClick={addNote} disabled={!noteText.trim()}><Icon name="plus" size={13} />Add note</button>
          </div>
        </div>
        {notes.length > 0 && (
          <div style={{ display: "grid", gap: 8 }}>
            {notes.map(n => (
              <div key={n.id} style={{ padding: "10px 12px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 10 }}>
                <div className="row between" style={{ alignItems: "flex-start", gap: 8 }}>
                  <div style={{ fontSize: 13.5, whiteSpace: "pre-wrap", flex: 1, minWidth: 0, wordBreak: "break-word" }}>{n.text}</div>
                  <button className="proj-del" style={{ background: "none", border: 0, color: "var(--fg-3)", cursor: "pointer", flexShrink: 0 }} onClick={() => delNote(n.id)} aria-label="Delete note"><Icon name="trash" size={13} /></button>
                </div>
                <div className="faint" style={{ fontSize: 11, marginTop: 5 }}>{CW_when(n.createdAt)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* files & photos */}
      <div className="card pad">
        <div className="row between" style={{ marginBottom: 12 }}>
          <span className="kicker kicker-teal"><Icon name="image" size={13} /> Photos &amp; files</span>
          <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}>
            <Icon name="plus" size={13} />{busy ? "Adding…" : "Add"}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" style={{ display: "none" }} onChange={onPick} />
        {files.length === 0
          ? <EmptyState icon="image" title="No photos or files yet" sub="Snap a photo of the yard or attach a survey — add a note on each so the crew knows what they're looking at." />
          : <div className="cust-files">
              {files.map(f => (
                <div key={f.id} className="cust-file">
                  <a className="cust-file-thumb" href={f.url} target="_blank" rel="noreferrer" download={f.kind === "file" ? f.name : undefined}
                    style={f.kind === "image" ? { backgroundImage: `url(${f.url})` } : undefined} title={f.name}>
                    {f.kind === "file" && <span className="cust-file-doc"><Icon name="clipboard" size={20} /><span>{(f.name.split(".").pop() || "file").toUpperCase()}</span></span>}
                    <button className="cust-file-del" onClick={e => { e.preventDefault(); delFile(f.id); }} aria-label="Delete"><Icon name="x" size={12} /></button>
                  </a>
                  <input className="cust-file-cap" value={f.caption || ""} placeholder="Add a note…" onChange={e => setCaption(f.id, e.target.value)} />
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

/* recent hub-thread preview on the customer's Messages tab (cloud-backed) */
function CustomerThreadPeek({ customer, onOpen }: any) {
  const DB: any = window.VFP_DB;
  const cloud = !!(DB && DB.ready && DB.chat);
  const [msgs, setMsgs] = React.useState<any>(null);   // null = loading
  const [loadErr, setLoadErr] = React.useState<any>(false);   // DB.chat.* threw — distinct from "no messages"
  const [tries, setTries] = React.useState<any>(0);
  React.useEffect(() => {
    let dead = false;
    if (!cloud) { setMsgs([]); return; }
    setMsgs(null); setLoadErr(false);
    (async () => {
      try {
        const rows = await DB.chat.list();
        const convo = (rows || []).find((r) => r.customer_id === customer.id);
        const m = convo ? await DB.chat.messages(convo.id) : [];
        if (!dead) setMsgs((m || []).slice(-5));
      } catch (e) {
        // a failed load is NOT an empty thread — say so and offer a retry
        if (!dead) { setLoadErr(true); setMsgs([]); }
      }
    })();
    return () => { dead = true; };
  }, [customer.id, cloud, tries]);
  return (
    <div className="card pad">
      {msgs == null
        ? <p className="muted" style={{ fontSize: 13, textAlign: "center", padding: 12 }}>Loading thread…</p>
        : loadErr
          ? <div className="row gap-8" style={{ justifyContent: "center", padding: 12, flexWrap: "wrap" }}>
              <span className="muted" style={{ fontSize: 13, color: "var(--red)" }}>Couldn't load messages</span>
              <button className="btn btn-outline btn-sm" onClick={() => setTries((t: number) => t + 1)}><Icon name="undo" size={13} />Retry</button>
            </div>
        : msgs.length === 0
          ? <div style={{ textAlign: "center" }}><EmptyState icon="mail" title="No messages yet" sub="Messages they send from their Customer Hub land here." /></div>
          : <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {msgs.map((m, i) => (
                <div key={i} className={"hub-msg " + (m.role === "customer" ? "company" : "me")} style={{ alignSelf: m.role === "customer" ? "flex-start" : "flex-end" }}>{m.body}</div>
              ))}
            </div>}
      <div style={{ textAlign: "center" }}>
        <button className="btn btn-primary btn-sm" style={{ marginTop: 4 }} onClick={onOpen}><Icon name="mail" size={13} />Open full thread</button>
      </div>
    </div>
  );
}

Object.assign(window, { Customers, CustomerDetail });

export { jobsForCustomer, Customers, CustomerDetail, CU_money0 };
